import React from 'react';
import './libs/lexical/plugins/mention/MentionsPlugin.scss';
// import './libs/lexical/plugins/native-emoji/NativeEmojiPlugin.scss';
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from "react-router-dom";
import ability from "./ability";
import { AbilityContext } from './ability-context';
import { FiltersLocalProvider } from './hooks/useFiltersLocal';
import { FiltersProvider } from './hooks/useFiltersStorage';
import { BreakpointProvider } from "./libs/breakpoint";
import history from './libs/history';
import Aqp from './libs/mk-react-aqp';
import RootRoutes from './routes';
import './sass/master.scss';
import AlertModal from "./ui/components2/AlertModal/AlertModal";
import { DebugProvider } from './ui/components2/Debug';
import ModalManager from './ui/components2/ModalManager';
import { Signin } from "./ui/components2/Signin/Signin";
import { Signup } from "./ui/components2/Signup/Signup";
import { DrawerProvider } from './ui/components2/DrawerProvider';
import AgeConfirmation from './ui/components2/AgeConfirmation/AgeConfirmation';
import { PostHogProvider} from 'posthog-js/react'
import EncourageSignup from './ui/components2/Encourage/EncourageSignup/EncourageSignup';
import ModalUserRestrictions from './ui/components2/Restrictions/ModalUserRestrictions';
import EncourageEmailConfirm from './ui/components2/Encourage/EncourageEmailConfirm/EncourageEmailConfirm';
import { NotifierProvider } from './ui/components2/UI/Notifier/Notifier';
import { DrimifyProvider } from './libs/drimify/DrimifyProvider';


Aqp.init(history);

const queries = {
    xs: '(max-width: 320px)',
    sm: '(max-width: 720px)',
    md: '(max-width: 1024px)',
    lg: '(max-width: 1200px)',
    or: '(orientation: portrait)'
}


const modalTypes = {
    EncourageSignup,
    EncourageEmailConfirm,
    AlertModal,
    Signin,
    Signup,
    AgeConfirmation,
    ModalUserRestrictions
};

const container = document.getElementById('root');
const root = createRoot(container);

const statusCodeErrorNoRetry = [400, 401, 403, 404, 423, 500];

function handleNetworkError(error) {
    if (window.location.pathname !== '/maintenance') {
        if (window.plausible) {
            const fullURL = new URL(error?.config.url, error?.config.baseURL);
            window.plausible('ERR_NETWORK', {
                props: {
                    url: error?.config.url,
                    path: fullURL.pathname,
                    search: fullURL.search,
                    method: error?.config.method
                }
            });
            console.warn(`ERR_NETWORK ${error?.config.method} ${error?.config.url}`);
        }
        sessionStorage.setItem('maintenance-backup-path', window.location.pathname);
        console.warn("Network Error: Redirecting to maintenance page.");
        window.location.href = '/maintenance';
    }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (count, error) => {
        console.warn(`[QUERY RETRY] Attempt #${count} – ${error?.config?.method} ${error?.config?.url}`);
        const statusCode =  error?.statusCode;
        return !statusCodeErrorNoRetry.includes(statusCode) && count < 2;
      },
      retryDelay: (attempt) => [1000, 3000, 5000][attempt] ?? 7000,
      onError: (error) => {
        if (error?.code === "ERR_NETWORK") {
          handleNetworkError(error);
        }
      }
    },
    // mutations: {
    //   retry: (count, error) => {
    //     console.warn(`[MUTATION RETRY] Attempt #${count} – ${error?.config?.method} ${error?.config?.url}`);
    //     const statusCode = error?.statusCode;
    //     return !statusCodeErrorNoRetry.includes(statusCode) && count < 1;
    //   },
    //   retryDelay: (attempt) => [1500, 3000, 5000][attempt] ?? 7000,
    //   onError: (error) => {
    //     if (error?.code === "ERR_NETWORK") {
    //       handleNetworkError(error);
    //     }
    //   }
    // }
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (error?.statusCode === 403) {
        window.location.href = "/";
      }
    }
  }),
//   mutationCache: new MutationCache({
//     onError: (error, mutation) => {
//       // Autres comportements globaux, optionnels
//       if (error?.statusCode === 403) {
//         window.location.href = "/";
//       }
//     }
//   })
});


root.render(
    <PostHogProvider 
        apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY}
        options={{
            api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
            person_profiles: 'identified_only'
        }}
        >
            
        <QueryClientProvider client={queryClient}>
            {process.env.NODE_ENV === "developmesnt" ?  <ReactQueryDevtools initialIsOpen={false} /> : null}
            <FiltersProvider>
                <FiltersLocalProvider>
                    <BrowserRouter>
                        <DebugProvider>
                            <BreakpointProvider queries={queries}>
                                <AbilityContext.Provider value={ability}>
                                    <DrimifyProvider>
                                        <ModalManager modalTypes={modalTypes}>
                                            <NotifierProvider>
                                                <DrawerProvider>
                                                        <RootRoutes />
                                                </DrawerProvider>
                                            </NotifierProvider>
                                        </ModalManager>
                                    </DrimifyProvider>
                                </AbilityContext.Provider>
                            </BreakpointProvider>
                        </DebugProvider>
                        
                    </BrowserRouter>
                </FiltersLocalProvider>
            </FiltersProvider>
        </QueryClientProvider>
    </PostHogProvider>
);